import type { RuleObject } from 'antd/es/form'
import { getJSON } from 'common/services/api'
import { useCallback } from 'react'
import { getQueryParams } from 'utils/getQueryParams'
import { CARERS_CLOCKED_IN_URL } from './queryConstants'

export const useCarersClockedInValidator = (
  instanceId: string,
  originalTeamMemberIds?: string[]
) => {
  const validateTeamMembers = useCallback(
    async (_: RuleObject, value: string[]) => {
      // Skip validation if no instanceId
      if (!instanceId) {
        return Promise.resolve()
      }

      // Normalise the value - handle both object format {value, label} and string format
      const normalisedValue = Array.isArray(value)
        ? value.map((member: string | { value: string }) =>
            typeof member === 'object' ? member.value : member
          )
        : []

      // If we have original team members, check for removals
      // We need to validate if any removed team members are clocked in
      if (originalTeamMemberIds && originalTeamMemberIds.length > 0) {
        const removedIds = originalTeamMemberIds.filter(
          (id: string) => !normalisedValue.includes(id)
        )

        // If team members are being removed, check if any of them are clocked in
        if (removedIds.length > 0) {
          // Check each removed team member individually to identify which specific ones are clocked in
          const clockedInIds: string[] = []

          // Check each removed ID individually
          for (const removedId of removedIds) {
            try {
              const params = getQueryParams({
                teamMembers: [removedId]
              })

              const response = await getJSON({
                url: `${CARERS_CLOCKED_IN_URL}/${instanceId}?${params}`,
                isRota: true
              })

              // Check if this specific team member is clocked in
              const isClockedIn =
                response === true ||
                (Array.isArray(response) && response.length > 0) ||
                (typeof response === 'object' &&
                  response !== null &&
                  Object.keys(response).length > 0)

              if (isClockedIn) {
                clockedInIds.push(removedId)
              }
            } catch (error) {
              // If individual check fails, skip this team member
            }
          }

          // If any team members are clocked in, reject with the specific IDs
          if (clockedInIds.length > 0) {
            return Promise.reject(
              Object.assign(
                new Error('Clocked in team member(s) cannot be unallocated.'),
                { clockedInIds }
              )
            )
          }
        } else {
          // No removals - validation passes (team members are still there)
          return Promise.resolve()
        }
      }

      // If no original team members, there's nothing to validate
      return Promise.resolve()
    },
    [instanceId, originalTeamMemberIds]
  )

  return { validator: validateTeamMembers }
}
